import React, { useState } from 'react';
import { Card, Input, Button, List, Typography, Form, Spin, message } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import CryptoUtils, { AESUtil } from '../utils/cryptoUtils';
import CryptoJS from 'crypto-js';

const { TextArea } = Input;
const { Title, Text } = Typography;

// 动画变体
const formControlVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: i * 0.1,
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  })
};

const resultVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
      delayChildren: 0.1,
      staggerChildren: 0.05
    }
  }
};

const resultItemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  }
};

const BruteForceDecrypt = () => {
  const [form] = Form.useForm();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]);

  // 所有可能的配置组合
  const CONFIG_COMBINATIONS = {
    keyLengths: [128, 192, 256],
    paddingModes: ['PKCS7', 'Zero', 'NoPadding'],
    encryptModes: ['CBC', 'ECB', 'CFB', 'OFB', 'CTR'] // 扩展加密模式
  };

  // 尝试解码 Base64
  const tryDecodeBase64 = (str) => {
    try {
      // 尝试直接解码
      const decoded = atob(str);
      return {
        success: true,
        result: decoded,
        type: 'standard'
      };
    } catch (e1) {
      try {
        // 尝试解码可能的 URL 安全的 Base64
        const urlSafeStr = str.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(urlSafeStr);
        return {
          success: true,
          result: decoded,
          type: 'urlsafe'
        };
      } catch (e2) {
        return {
          success: false,
          result: str,
          type: 'none'
        };
      }
    }
  };

  // 尝试解密
  const tryDecrypt = async (values) => {
    setLoading(true);
    setResults([]);
    setDebugInfo([]);
    const newResults = [];
    let totalAttempts = 0;
    const newDebugInfo = [];

    try {
      const { ciphertext, key, iv } = values;
      
      // 准备可能的密文格式
      const ciphertextVariations = [
        { text: ciphertext, type: '原始密文' }
      ];

      // 尝试 Base64 解码
      const base64Result = tryDecodeBase64(ciphertext);
      if (base64Result.success) {
        ciphertextVariations.push({
          text: base64Result.result,
          type: 'Base64解码'
        });
      }

      // 如果看起来是十六进制字符串，转换为原始字节
      if (/^[0-9A-Fa-f]+$/.test(ciphertext)) {
        const hexDecoded = ciphertext.match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('');
        if (hexDecoded) {
          ciphertextVariations.push({
            text: hexDecoded,
            type: 'Hex解码'
          });
        }
      }

      // 分析密钥长度情况，使用不同的AES密钥长度
      const keyAnalysis128 = AESUtil.analyzeKeyHandling(key, 128);
      const keyAnalysis192 = AESUtil.analyzeKeyHandling(key, 192);
      const keyAnalysis256 = AESUtil.analyzeKeyHandling(key, 256);
      
      // 生成密钥长度建议
      const suggestedKeyLength = generateKeySuggestion(keyAnalysis128.actualBytes);
      
      newDebugInfo.push(`📏 密钥实际长度: ${keyAnalysis128.actualBytes}字节 (${keyAnalysis128.actualBits}位)`);
      newDebugInfo.push(suggestedKeyLength);

      // CryptoJS处理信息
      newDebugInfo.push('🔄 CryptoJS处理方式:');
      newDebugInfo.push(`  • AES-128: ${keyAnalysis128.cryptoJSHandling}`);
      newDebugInfo.push(`  • AES-192: ${keyAnalysis192.cryptoJSHandling}`);
      newDebugInfo.push(`  • AES-256: ${keyAnalysis256.cryptoJSHandling}`);

      if (keyAnalysis128.cryptoJSHandling === keyAnalysis192.cryptoJSHandling && 
          keyAnalysis192.cryptoJSHandling === keyAnalysis256.cryptoJSHandling) {
        newDebugInfo.push('⚠️ 注意: 三种密钥长度配置可能产生相同的结果，因为CryptoJS对密钥进行了相同处理');
      }

      // 详细的密钥处理分析信息
      console.log('密钥分析 (AES-128):', keyAnalysis128);
      console.log('密钥分析 (AES-192):', keyAnalysis192);
      console.log('密钥分析 (AES-256):', keyAnalysis256);

      console.log('开始解密过程：', {
        原始密文长度: ciphertext.length,
        密钥长度: key.length,
        IV长度: iv ? iv.length : '未提供',
        待尝试密文格式数: ciphertextVariations.length
      });

      // 遍历所有可能的组合
      for (const cipherVariation of ciphertextVariations) {
        for (const keyLength of CONFIG_COMBINATIONS.keyLengths) {
          for (const paddingMode of CONFIG_COMBINATIONS.paddingModes) {
            for (const encryptMode of CONFIG_COMBINATIONS.encryptModes) {
              totalAttempts++;
              console.log(`\n尝试配置组合 #${totalAttempts}:`, {
                密文类型: cipherVariation.type,
                密钥长度: keyLength,
                填充模式: paddingMode,
                加密模式: encryptMode
              });

              try {
                // 对于某些模式，如果没有提供 IV，就使用空字节
                const effectiveIV = (!iv && ['CBC', 'CFB', 'OFB', 'CTR'].includes(encryptMode))
                  ? '\0'.repeat(16)
                  : iv || '';

                // 分析当前配置的密钥处理情况
                const keyAnalysis = AESUtil.analyzeKeyHandling(key, keyLength);
                console.log(`密钥分析 (AES-${keyLength}):`, keyAnalysis);

                // 直接传递参数，而不是对象
                const result = CryptoUtils.decrypt(
                  cipherVariation.text,
                  key,
                  effectiveIV,
                  paddingMode,
                  encryptMode
                );

                // 验证解密结果是否是有效的 UTF-8 文本
                if (result && /^[\x20-\x7E\u4E00-\u9FFF\s]*$/.test(result)) {
                  console.log('解密成功！结果：', {
                    明文前20字符: result.substring(0, 20) + '...',
                    明文长度: result.length
                  });

                  newResults.push({
                    id: `${cipherVariation.type}-${keyLength}-${paddingMode}-${encryptMode}`,
                    text: result,
                    config: {
                      cipherType: cipherVariation.type,
                      keyLength,
                      paddingMode,
                      encryptMode,
                      actualKeyBits: keyAnalysis.actualBits,
                      cryptoJSKeyBytes: keyAnalysis.cryptoJSBytes
                    }
                  });
                }
              } catch (error) {
                console.log('该组合解密失败：', error.message);
                continue;
              }
            }
          }
        }
      }

      newDebugInfo.push(`尝试次数: ${totalAttempts}`);
      newDebugInfo.push(`成功次数: ${newResults.length}`);

      console.log('\n解密过程完成：', {
        尝试次数: totalAttempts,
        成功次数: newResults.length
      });

      if (newResults.length === 0) {
        message.warning('未找到任何可用的解密结果');
      } else {
        message.success(`找到 ${newResults.length} 个可能的解密结果`);
      }

      setResults(newResults);
      setDebugInfo(newDebugInfo);
    } catch (error) {
      console.error('解密过程出错:', error);
      message.error('解密过程出错');
    } finally {
      setLoading(false);
    }
  };

  // 生成密钥长度建议
  const generateKeySuggestion = (actualBytes) => {
    // AES标准密钥长度: 16字节, 24字节, 32字节
    const standardKeyLengths = [
      { bytes: 16, bits: 128 },
      { bytes: 24, bits: 192 },
      { bytes: 32, bits: 256 }
    ];
    
    // 如果刚好是标准长度
    for (const length of standardKeyLengths) {
      if (actualBytes === length.bytes) {
        return `✅ 您的密钥长度为标准的AES-${length.bits}长度（${length.bytes}字节）`;
      }
    }
    
    // 如果不是标准长度，找出最接近的标准长度
    let closestLength = standardKeyLengths[0];
    let minDiff = Math.abs(actualBytes - closestLength.bytes);
    
    for (let i = 1; i < standardKeyLengths.length; i++) {
      const diff = Math.abs(actualBytes - standardKeyLengths[i].bytes);
      if (diff < minDiff) {
        minDiff = diff;
        closestLength = standardKeyLengths[i];
      }
    }
    
    // 确定是需要扩展还是缩短
    if (actualBytes < closestLength.bytes) {
      return `⚠️ 您的密钥长度为${actualBytes}字节，推荐扩展到${closestLength.bytes}字节（AES-${closestLength.bits}）`;
    } else {
      return `⚠️ 您的密钥长度为${actualBytes}字节，推荐缩短到${closestLength.bytes}字节（AES-${closestLength.bits}）`;
    }
  };

  // 结果列表项标题
  const renderResultTitle = (item) => (
    <div>
      <Text>
        配置：{item.config.cipherType} / {item.config.keyLength}位 / {item.config.paddingMode} / {item.config.encryptMode}
      </Text>
      {item.config.actualKeyBits !== item.config.keyLength && (
        <Text type="warning" style={{ marginLeft: '8px' }}>
          (提示：您当前密钥长度为{item.config.actualKeyBits}位，应使用{item.config.keyLength}位密钥（{item.config.keyLength/8}字节）)
        </Text>
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Card style={{ marginBottom: '24px' }}>
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <Title level={4}>解密一把梭模式</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
            该模式将尝试所有可能的配置组合来解密，帮助你在不确定加密配置的情况下找到正确的解密结果。
            支持自动识别 Base64 编码和十六进制编码的密文。
          </Text>
        </motion.div>

        <Form
          form={form}
          layout="vertical"
          onFinish={tryDecrypt}
        >
          <motion.div
            custom={0}
            variants={formControlVariants}
            initial="hidden"
            animate="visible"
          >
            <Form.Item
              name="ciphertext"
              label="密文"
              rules={[{ required: true, message: '请输入需要解密的密文' }]}
            >
              <TextArea
                rows={4}
                placeholder="请输入需要解密的密文（支持原始格式、Base64编码、十六进制编码）"
              />
            </Form.Item>
          </motion.div>

          <motion.div
            custom={1}
            variants={formControlVariants}
            initial="hidden"
            animate="visible"
          >
            <Form.Item
              name="key"
              label="密钥"
              rules={[{ required: true, message: '请输入密钥' }]}
              extra="AES-128需要16字节(128位)、AES-192需要24字节(192位)、AES-256需要32字节(256位)的密钥"
            >
              <Input placeholder="请输入密钥" maxLength={32} />
            </Form.Item>
          </motion.div>

          <motion.div
            custom={2}
            variants={formControlVariants}
            initial="hidden"
            animate="visible"
          >
            <Form.Item
              name="iv"
              label="初始向量 (可选)"
              extra="某些加密模式需要16字节的初始向量"
            >
              <Input placeholder="请输入初始向量（可选，某些加密模式需要）" maxLength={16} />
            </Form.Item>
          </motion.div>

          <motion.div
            custom={3}
            variants={formControlVariants}
            initial="hidden"
            animate="visible"
          >
            <Form.Item>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ display: 'inline-block' }}
              >
                <Button type="primary" htmlType="submit" loading={loading}>
                  开始尝试解密
                </Button>
              </motion.div>
            </Form.Item>
          </motion.div>
        </Form>

        {debugInfo.length > 0 && (
          <div style={{ marginTop: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '4px', marginBottom: '16px' }}>
            <Title level={5}>密钥分析</Title>
            <ul>
              {debugInfo.map((info, index) => (
                <li key={index}>{info}</li>
              ))}
            </ul>
            <div style={{ marginTop: '8px', backgroundColor: '#fff', padding: '8px', borderRadius: '4px', border: '1px solid #d9e8f2' }}>
              <Text strong>标准密钥长度参考：</Text>
              <ul style={{ marginTop: '4px' }}>
                <li><Text>AES-128: 16字节(128位)密钥</Text></li>
                <li><Text>AES-192: 24字节(192位)密钥</Text></li>
                <li><Text>AES-256: 32字节(256位)密钥</Text></li>
              </ul>
              <Text type="secondary">
                如果您的密钥长度不是标准长度，CryptoJS会自动填充或截断密钥，这可能导致不同密钥长度设置下产生相同的解密结果。
              </Text>
            </div>
          </div>
        )}

        <Spin spinning={loading}>
          <AnimatePresence>
            {results.length > 0 && (
              <motion.div
                key="results"
                variants={resultVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
              >
                <List
                  header={
                    <motion.div 
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.3 }}
                    >
                      <Title level={5}>解密结果（{results.length}个）</Title>
                    </motion.div>
                  }
                  dataSource={results}
                  renderItem={(item, index) => (
                    <motion.div
                      key={item.id}
                      variants={resultItemVariants}
                      custom={index}
                      layout
                    >
                      <List.Item>
                        <motion.div
                          whileHover={{ scale: 1.01, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                          style={{ width: '100%' }}
                        >
                          <Card
                            size="small"
                            title={renderResultTitle(item)}
                            style={{ width: '100%' }}
                          >
                            <Text copyable style={{ display: 'block' }}>{item.text}</Text>
                          </Card>
                        </motion.div>
                      </List.Item>
                    </motion.div>
                  )}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Spin>
      </Card>
    </motion.div>
  );
};

export default BruteForceDecrypt; 